import { useState, useEffect } from "react";
import { onSnapshot, deleteDoc, updateDoc } from "firebase/firestore";
import { ref, getDownloadURL, deleteObject } from "firebase/storage";
import { storage } from "../firebase";
import {
  requireTenantBandContext,
  tenantSheetMusicCollectionRef,
  tenantSheetMusicDocRef,
  tenantStoragePath,
} from "../tenantContext";

const BPM_RANGES = ["< 70", "70–90", "90–110", "110–130", "130–150", "150+"];

function bpmMatchesRange(bpm, range) {
  const val = Number(bpm);
  if (!bpm || isNaN(val)) return false;
  if (range === "< 70") return val < 70;
  if (range === "150+") return val >= 150;
  const [lo, hi] = range.split("–").map(Number);
  return val >= lo && val < hi;
}

function extractStoragePathFromUrl(url) {
  try {
    if (!url) return "";
    if (url.startsWith("gs://")) {
      const firstSlash = url.indexOf("/", 5);
      return firstSlash === -1 ? "" : decodeURIComponent(url.slice(firstSlash + 1));
    }
    const parsed = new URL(url);
    const marker = "/o/";
    const markerIndex = parsed.pathname.indexOf(marker);
    if (markerIndex === -1) return "";
    return decodeURIComponent(parsed.pathname.slice(markerIndex + marker.length));
  } catch {
    return "";
  }
}

export function useSongs(tenantContext) {
  const [songs, setSongs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [searchTerm, setSearchTerm] = useState("");
  const [filterKey, setFilterKey] = useState("");
  const [filterBpm, setFilterBpm] = useState("");

  useEffect(() => {
    if (!tenantContext?.bandId) {
      setSongs([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const tenant = requireTenantBandContext(tenantContext);
    const unsub = onSnapshot(
      tenantSheetMusicCollectionRef(tenant),
      (querySnapshot) => {
        const list = querySnapshot.docs.map((d) => {
          const data = d.data();
          return { id: d.id, ...data, pdfUrl: data.pdfUrl || data.pdfPath || "" };
        });
        setSongs(list.sort((a, b) => a.title.toLowerCase() < b.title.toLowerCase() ? -1 : 1));
        setIsLoading(false);
      },
      (err) => {
        console.error("Error fetching songs:", err);
        setError("Failed to fetch songs. Please try again.");
        setIsLoading(false);
      }
    );
    return unsub;
  }, [tenantContext?.bandId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  const sortedSongs = [...songs].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const aVal = a[sortConfig.key]?.toString().toLowerCase() || "";
    const bVal = b[sortConfig.key]?.toString().toLowerCase() || "";
    if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
    if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const filteredSongs = sortedSongs.filter((song) => {
    const q = searchTerm.trim().toLowerCase();
    const matchesSearch =
      !q || song.title?.toLowerCase().includes(q) || song.artist?.toLowerCase().includes(q);
    const matchesKey = !filterKey || song.key === filterKey;
    const matchesBpm = !filterBpm || bpmMatchesRange(song.bpm, filterBpm);
    return matchesSearch && matchesKey && matchesBpm;
  });

  const resolveSongPdfUrl = async (song) => {
    const existingUrl = song?.pdfUrl || song?.pdfPath || "";
    if (!existingUrl) throw new Error(`Missing PDF URL for ${song?.title || "song"}`);

    const storagePath = extractStoragePathFromUrl(existingUrl);
    if (!storagePath) return existingUrl;

    const fileName = storagePath.split("/").pop();
    const candidatePaths = [storagePath];
    if (fileName && tenantContext?.bandId) {
      candidatePaths.push(tenantStoragePath(tenantContext, "sheet_music", fileName));
    }

    let freshUrl = "";
    for (const candidatePath of [...new Set(candidatePaths)]) {
      try {
        freshUrl = await getDownloadURL(ref(storage, candidatePath));
        break;
      } catch {}
    }

    if (!freshUrl) return existingUrl;

    if (freshUrl !== existingUrl && song?.id) {
      try {
        const tenant = requireTenantBandContext(tenantContext);
        const songRef = tenantSheetMusicDocRef(tenant, song.id);
        await updateDoc(songRef, { pdfUrl: freshUrl });
        setSongs((prev) => prev.map((s) => (s.id === song.id ? { ...s, pdfUrl: freshUrl } : s)));
      } catch (err) {
        console.error("Could not persist refreshed PDF URL:", err);
      }
    }

    return freshUrl;
  };

  const patchSong = (id, updates) => {
    setSongs((prev) => prev.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  const deleteSong = async (song) => {
    let storagePath = song.pdfPath || extractStoragePathFromUrl(song.pdfUrl || "");
    if (storagePath) {
      try {
        await deleteObject(ref(storage, storagePath));
      } catch (err) {
        console.warn("Storage delete failed (file may already be gone):", err);
      }
    }
    const tenant = requireTenantBandContext(tenantContext);
    await deleteDoc(tenantSheetMusicDocRef(tenant, song.id));
    setSongs((prev) => prev.filter((s) => s.id !== song.id));
  };

  return {
    songs,
    filteredSongs,
    bpmRanges: BPM_RANGES,
    sortConfig,
    handleSort,
    searchTerm,
    setSearchTerm,
    filterKey,
    setFilterKey,
    filterBpm,
    setFilterBpm,
    deleteSong,
    patchSong,
    resolveSongPdfUrl,
    isLoading,
    error,
    clearError: () => setError(""),
  };
}
